import { useState, useCallback } from 'react';
import PatientForm from './components/PatientForm';
import NotePreview from './components/NotePreview';
import ExportButtons from './components/ExportButtons';
import { PatientData, DEFAULT_PATIENT_DATA } from './types';
import { generateNote } from './services/api';

type ViewMode = 'form' | 'preview' | 'split';

export default function App() {
  const [patientData, setPatientData] = useState<PatientData>(DEFAULT_PATIENT_DATA);
  const [generatedNote, setGeneratedNote] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('split');
  const [isEditable, setIsEditable] = useState(false);

  const handleGenerateNote = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await generateNote(patientData);
      setGeneratedNote(response.note);
      if (viewMode === 'form') {
        setViewMode('split');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate note');
    } finally {
      setIsLoading(false);
    }
  }, [patientData, viewMode]);

  const handleClearForm = () => {
    setPatientData(DEFAULT_PATIENT_DATA);
    setGeneratedNote('');
    setError(null);
  };

  const handleLoadSampleData = () => {
    setPatientData({
      patientName: 'Jane Smith',
      dateOfBirth: '1985-03-15',
      mrn: 'MRN-789456',
      dateOfService: new Date().toISOString().split('T')[0],
      provider: 'Dr. Sarah Johnson, MD',
      chiefComplaint: 'Feeling depressed and anxious for the past 3 months',
      historyOfPresentIllness: 'Patient is a 38-year-old female presenting with complaints of persistent low mood, decreased energy, and difficulty sleeping for the past 3 months. She reports the onset coincided with a job loss. She describes her mood as "sad most of the time" and rates it 4/10. She has lost interest in activities she previously enjoyed, including reading and spending time with friends. Sleep is fragmented with early morning awakening around 4 AM. Appetite is decreased with unintentional 8 lb weight loss. She denies any substance use. No prior psychiatric history. No family history of completed suicide.',
      symptoms: [
        'Depressed mood',
        'Anhedonia (loss of interest/pleasure)',
        'Sleep disturbance',
        'Appetite changes',
        'Fatigue/low energy',
        'Difficulty concentrating',
        'Anxiety',
        'Social withdrawal'
      ],
      medications: [
        { id: '1', name: 'Lisinopril', dose: '10mg', frequency: 'Once daily' },
        { id: '2', name: 'Metformin', dose: '500mg', frequency: 'Twice daily' }
      ],
      mentalStatusExam: {
        appearance: 'Casually dressed',
        behavior: 'Cooperative',
        speech: 'Normal rate and rhythm',
        mood: 'Depressed',
        affect: 'Congruent with mood',
        thoughtProcess: 'Linear and goal-directed',
        thoughtContent: 'Denies SI/HI',
        cognition: 'Alert and oriented x3',
        insightJudgment: 'Fair'
      },
      assessment: 'F32.1 Major depressive disorder, single episode, moderate\nF41.1 Generalized anxiety disorder',
      plan: '1. Start Sertraline 50mg daily, increase to 100mg after 1 week if tolerated\n2. Refer to cognitive behavioral therapy\n3. Sleep hygiene education provided\n4. Return to clinic in 2 weeks for medication check\n5. Patient advised to go to ER or call 988 if experiencing suicidal thoughts'
    });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Clinical Notes Generator</h1>
              <p className="text-sm text-gray-500">Portfolio Demo - Psychiatric Evaluation Notes</p>
            </div>

            {/* View Toggle */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 mr-2">View:</span>
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('form')}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    viewMode === 'form'
                      ? 'bg-white text-clinical-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Form
                </button>
                <button
                  onClick={() => setViewMode('split')}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    viewMode === 'split'
                      ? 'bg-white text-clinical-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Split
                </button>
                <button
                  onClick={() => setViewMode('preview')}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    viewMode === 'preview'
                      ? 'bg-white text-clinical-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Preview
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Action Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleGenerateNote}
                disabled={isLoading}
                className="btn-primary flex items-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Generate Note
                  </>
                )}
              </button>

              <button
                onClick={handleLoadSampleData}
                className="btn-secondary flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Load Sample Data
              </button>

              <button
                onClick={handleClearForm}
                className="btn-secondary flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Clear Form
              </button>
            </div>

            {generatedNote && (
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={isEditable}
                    onChange={(e) => setIsEditable(e.target.checked)}
                    className="w-4 h-4 text-clinical-600 rounded focus:ring-clinical-500"
                  />
                  Edit Mode
                </label>
                <ExportButtons
                  note={generatedNote}
                  patientName={patientData.patientName}
                  disabled={!generatedNote}
                />
              </div>
            )}
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
        </div>

        {/* Content Area */}
        <div className={`grid gap-6 ${
          viewMode === 'split' ? 'lg:grid-cols-2' : 'grid-cols-1'
        }`}>
          {/* Form */}
          {(viewMode === 'form' || viewMode === 'split') && (
            <div className={viewMode === 'split' ? 'lg:max-h-[calc(100vh-220px)] lg:overflow-y-auto' : ''}>
              <PatientForm data={patientData} onChange={setPatientData} />
            </div>
          )}

          {/* Preview */}
          {(viewMode === 'preview' || viewMode === 'split') && (
            <div className={viewMode === 'split' ? 'lg:sticky lg:top-[140px]' : ''}>
              <NotePreview
                note={generatedNote}
                isLoading={isLoading}
                onNoteChange={setGeneratedNote}
                isEditable={isEditable}
              />
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-8">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-500">
            <div>
              <p className="font-medium text-gray-700">Clinical Notes Generator Demo</p>
              <p>Portfolio demonstration project - Not for clinical use</p>
            </div>
            <div className="text-center sm:text-right">
              <p>All patient data is simulated/fictional</p>
              <p className="text-clinical-600">
                <a href="https://clinicaldemo.portfolio.redshadegames.com" className="hover:underline">
                  clinicaldemo.portfolio.redshadegames.com
                </a>
              </p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 text-center text-sm text-gray-500">
            <p className="mb-2">
              Built with React, TypeScript, Node.js, Express, Tailwind CSS, and jsPDF
            </p>
            <p>
              Created by{' '}
              <a href="https://portfolio.redshadegames.com/" className="text-clinical-600 hover:underline">
                Joshua Jenks
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
